import { useState, useCallback, useEffect, useRef } from 'react';
import { generateProductVideo, pollVideoStatus } from '../services/geminiService';

const VEO_LOADING_MESSAGES = [
  "Warming up the virtual studio...",
  "Directing the digital lighting...",
  "Polishing the product pixels...",
  "Rendering the main sequence...",
  "Adding the final marketing magic...",
  "Almost there, preparing your video...",
];

if (typeof window !== 'undefined' && !window.aistudio) {
  console.log('AI Studio context not found. Mocking for local development.');
  window.aistudio = {
    hasSelectedApiKey: async () => true,
    openSelectKey: async () => {
      console.log('openSelectKey mocked: This is a placeholder for local development.');
      alert("In a local environment, please set your API_KEY in the environment variables. The API key selector is not available.");
    },
  };
}

export const useVeo = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState(null);
  const [promptForApiKey, setPromptForApiKey] = useState(false);
  const pollingIntervalRef = useRef(null);
  const messageIntervalRef = useRef(null);

  const cleanupIntervals = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => cleanupIntervals();
  }, []);

  const selectApiKey = useCallback(async () => {
    try {
      await window.aistudio.openSelectKey();
      setPromptForApiKey(false); 
    } catch (e) {
      console.error("Error opening API key selection:", e);
      setError("Failed to open API key selection. Please try again.");
    }
  }, []);

  const generateVideo = useCallback(async (uploadedFile, aspectRatio, prompt) => {
    const hasKey = await window.aistudio.hasSelectedApiKey();
    if (!hasKey) {
        setPromptForApiKey(true);
        return;
    }

    setIsGenerating(true);
    setVideoUrl(null);
    setError(null);
    setStatusMessage(VEO_LOADING_MESSAGES[0]);

    let messageIndex = 0;
    messageIntervalRef.current = window.setInterval(() => {
        messageIndex = (messageIndex + 1) % VEO_LOADING_MESSAGES.length;
        setStatusMessage(VEO_LOADING_MESSAGES[messageIndex]);
    }, 5000);

    try {
      let operation = await generateProductVideo(uploadedFile, aspectRatio, prompt);

      pollingIntervalRef.current = window.setInterval(async () => {
        try {
            operation = await pollVideoStatus(operation);

            if (operation.done) {
                cleanupIntervals();
                const downloadLink = operation.response?.generatedVideos?.[0]?.video?.uri;
                if (downloadLink) {
                    setStatusMessage("Fetching your video...");
                    const response = await fetch(`${downloadLink}&key=${process.env.REACT_APP_API_KEY}`);
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    setVideoUrl(url);
                } else {
                    throw new Error("Video generation completed but no video URL was found.");
                }
                setIsGenerating(false);
            }
        } catch(pollError) {
            cleanupIntervals();
            setIsGenerating(false);
            if (pollError.message?.includes("Requested entity was not found.")) {
                setError("Your API key is invalid or has been revoked. Please select a valid key.");
                setPromptForApiKey(true);
            } else {
                setError("An error occurred while checking video status.");
            }
            console.error("Polling error:", pollError);
        }
      }, 10000);

    } catch (err) {
      cleanupIntervals();
      setIsGenerating(false);
      setError('Failed to start video generation. Please check your setup and try again.');
      console.error(err);
    }
  }, []);

  return {
    generateVideo,
    isGenerating,
    videoUrl,
    statusMessage,
    error,
    promptForApiKey,
    selectApiKey,
  };
};